// OptionsTable.tsx - Redesigned to match GroupItemsTable styling

import React, { useEffect, useState, useRef } from "react";
import { FaTrash } from "react-icons/fa";
import { Table, Form } from 'react-bootstrap';
import { 
  GetModifierOptionsByModifierId,
  UpdateModifierOptionNameByOptionId,
  UpdateModifierOptionDescriptionByOptionId,
  UpdateModifierOptionPriceModifierByOptionId,
} from "../../../../../../../../crud/menu_api/selection_modifiers";
import { toast } from "react-toastify";
import RemoveOptionFromModifierModal from "./components/RemoveOptionFromModifierModal/RemoveOptionFromModifierModal";
import './OptionsTable.scss';

interface Modifier {
  modifier_id: number;
  modifier_name: string;
  modifier_description?: string;
  local_id: number;
}

interface ModifierOption {
  option_id: number;
  modifier_id: number;
  option_name: string;
  option_description?: string;
  price_modifier: number;
}

interface TableProps {
  mod: Modifier;
  refreshTrigger?: number;
}

const OptionsTable: React.FC<TableProps> = ({
  mod,
  refreshTrigger = 0,
}) => {
  const [options, setOptions] = useState<ModifierOption[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Input refs for focus management
  const inputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ModifierOption | null>(null);

  useEffect(() => {
    setLoading(true);
    GetModifierOptionsByModifierId(mod.modifier_id)
      .then((response) => {
        setOptions(response.data);
      })
      .catch((error) => {
        console.error("Error fetching modifier options", error);
        toast.error("Error al cargar las opciones");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [mod.modifier_id, refreshTrigger]);

  // Handle local update (optimistic UI)
  const handleLocalUpdate = (optionId: number, field: string, value: string | number) => {
    const updatedOptions = options.map((option) => {
      if (option.option_id === optionId) {
        return { ...option, [field]: value };
      }
      return option;
    });
    setOptions(updatedOptions);
  };

  // Handle confirm update (API call)
  const handleConfirmUpdate = (optionId: number, field: string, value: string | number) => {
    const option = options.find(o => o.option_id === optionId);
    if (!option) return;

    // Check if value actually changed
    const originalValue = option[field as keyof ModifierOption];
    if (originalValue === value) return;

    switch (field) {
      case 'option_name':
        if (typeof value === 'string' && value.trim() === '') {
          toast.error('El nombre no puede estar vacío');
          handleLocalUpdate(optionId, field, originalValue as string);
          return;
        }
        UpdateModifierOptionNameByOptionId({ option_name: value as string }, optionId)
          .then(() => {
            toast.success('Nombre actualizado');
          })
          .catch((error) => {
            console.error("Error updating name", error);
            toast.error("Error al actualizar el nombre");
            handleLocalUpdate(optionId, field, originalValue as string);
          });
        break;

      case 'option_description':
        UpdateModifierOptionDescriptionByOptionId({ option_description: value as string }, optionId)
          .then(() => {
            toast.success('Descripción actualizada');
          })
          .catch((error) => {
            console.error("Error updating description", error);
            toast.error("Error al actualizar la descripción");
            handleLocalUpdate(optionId, field, originalValue as string);
          });
        break;

      case 'price_modifier':
        UpdateModifierOptionPriceModifierByOptionId(value as number, optionId)
          .then(() => {
            toast.success('Precio actualizado');
          })
          .catch((error) => {
            console.error("Error updating price", error);
            toast.error("Error al actualizar el precio");
            handleLocalUpdate(optionId, field, originalValue as number);
          });
        break;
    }
  };

  const handleRemoveOption = (option: ModifierOption) => {
    setShowDeleteModal(true);
    setSelectedOption(option);
  };

  // Render input for name
  const renderNameInput = (option: ModifierOption) => (
    <Form.Control
      ref={(ref: HTMLInputElement | null) => {
        inputRefs.current[`name_${option.option_id}`] = ref;
      }}
      type="text"
      value={option.option_name}
      onChange={(e) => handleLocalUpdate(option.option_id, 'option_name', e.target.value)}
      onBlur={(e) => handleConfirmUpdate(option.option_id, 'option_name', e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleConfirmUpdate(option.option_id, 'option_name', (e.target as HTMLInputElement).value);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="name-input"
      placeholder="Nombre de la opción"
    />
  );

  // Render input for description
  const renderDescriptionInput = (option: ModifierOption) => (
    <Form.Control
      ref={(ref: HTMLInputElement | null) => {
        inputRefs.current[`desc_${option.option_id}`] = ref;
      }}
      type="text"
      value={option.option_description || ''}
      onChange={(e) => handleLocalUpdate(option.option_id, 'option_description', e.target.value)}
      onBlur={(e) => handleConfirmUpdate(option.option_id, 'option_description', e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleConfirmUpdate(option.option_id, 'option_description', (e.target as HTMLInputElement).value);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="description-input"
      placeholder="Descripción opcional"
    />
  );

  // Render input for price
  const renderPriceInput = (option: ModifierOption) => (
    <Form.Control
      ref={(ref: HTMLInputElement | null) => {
        inputRefs.current[`price_${option.option_id}`] = ref;
      }}
      type="number"
      step="0.01"
      min="0"
      value={option.price_modifier}
      onChange={(e) => handleLocalUpdate(option.option_id, 'price_modifier', parseFloat(e.target.value) || 0)}
      onBlur={(e) => handleConfirmUpdate(option.option_id, 'price_modifier', parseFloat(e.target.value) || 0)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleConfirmUpdate(option.option_id, 'price_modifier', parseFloat((e.target as HTMLInputElement).value) || 0);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="price-input"
      placeholder="0"
    />
  );

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <p>Cargando opciones del modificador...</p>
      </div>
    );
  }

  // Empty state
  if (options.length === 0) {
    return (
      <div className="options-empty-state">
        <p>No hay opciones en este modificador. Añade una opción para comenzar.</p>
      </div>
    );
  }

  return (
    <>
      <Table responsive className="options-configuration-table">
        <thead>
          <tr>
            <th className="option-name-col">Nombre</th>
            <th className="description-col">Descripción</th>
            <th className="price-col">Precio</th>
            <th className="actions-col"></th>
          </tr>
        </thead>
        <tbody>
          {options.map((option, index) => (
            <tr key={option.option_id} className={index % 2 === 0 ? 'even' : 'odd'}>
              <td className="option-name-cell">
                {renderNameInput(option)}
              </td>
              <td className="description-cell">
                {renderDescriptionInput(option)}
              </td>
              <td className="price-cell">
                {renderPriceInput(option)}
              </td>
              <td className="actions-cell">
                <button
                  className="delete-option-btn"
                  onClick={() => handleRemoveOption(option)}
                  title={`Eliminar ${option.option_name}`}
                  type="button"
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      {showDeleteModal && mod && selectedOption && (
        <RemoveOptionFromModifierModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          mod={mod}
          option={selectedOption}
          setOptions={setOptions}
          options={options}
        />
      )}
    </>
  );
};

export default OptionsTable;
